import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ChevronRight, FileText } from 'lucide-react';

// Each policy is a list of (heading, paragraph-or-list) blocks. Kept terse,
// readable, and grounded in what FAT BUSTERS actually does — manual PayID
// reconciliation, no third-party processors, no card data, 18+ research use.
const POLICIES = {
  privacy: {
    title: 'Privacy Policy',
    intro: 'How FAT BUSTERS collects, uses, and protects your personal information.',
    updated: '13 February 2026',
    sections: [
      ['What we collect', [
        'Account information: your name, email, phone number, and shipping address.',
        'Order information: the products you have purchased and the PayID reference code we generate to match your payment.',
        'Verification information: confirmation that you are 18+ and that you have read the dosing & safety guide.',
        'Technical: standard browser session cookies (HttpOnly) used to keep you signed in.',
      ]],
      ['What we do NOT collect', [
        'No card numbers, BSB, account numbers, or CVV — payments are made directly bank-to-bank by PayID. FAT BUSTERS never sees or stores your banking credentials.',
        'No medical data, prescription history, or health diagnostics.',
      ]],
      ['How we use it', [
        'To process and ship your orders.',
        'To send order receipts, payment confirmations, and shipping updates by SMS and email.',
        'To send up to one reminder SMS per month if it has been 25+ days since your last order.',
        'To verify the 18+ requirement and reduce fraud.',
      ]],
      ['Who we share with', [
        'Twilio — to deliver SMS messages to the phone number you provided.',
        'Resend — to deliver transactional emails to the email you provided.',
        'AusPost / Sendle / chosen courier — for shipping address only.',
        'Australian law-enforcement or regulators when required by law.',
        'We do not sell or rent your data to third parties.',
      ]],
      ['How long we keep it', [
        'Order records: 7 years (Australian tax-record retention requirement).',
        'Account information: until you ask us to delete it or your account becomes inactive for 24 months.',
      ]],
      ['Your rights', [
        'You can request a copy of the personal data we hold on you.',
        'You can request correction or deletion of your account.',
        'You can opt out of marketing SMS by replying STOP at any time.',
        'Contact us at the email below to exercise any of these rights.',
      ]],
      ['Contact', [
        'Privacy queries: use the Contact Us form. We aim to respond within 30 days as required by the Privacy Act 1988 (Cth).',
      ]],
    ],
  },

  terms: {
    title: 'Terms of Service',
    intro: 'The agreement between you and FAT BUSTERS when you use this site and purchase products.',
    updated: '13 February 2026',
    sections: [
      ['Eligibility', [
        'You must be 18 years or older to create an account and purchase products. By placing an order you confirm you meet this requirement.',
        'You agree the products are purchased as in-vitro research / reference materials and used in accordance with all applicable laws in your jurisdiction.',
      ]],
      ['Product use & no medical advice', [
        'FAT BUSTERS does not provide medical advice, diagnosis, or treatment.',
        'No guarantees, express or implied, are made regarding outcomes from using our products. Individual results vary.',
        'You should consult a licensed medical practitioner before use. Use of the products is entirely at your own risk.',
        'You agree to the dosing and storage guidance set out in our Dosing & Safety Guide and confirm this at the point of purchase.',
      ]],
      ['Orders & PayID payment', [
        'All payments are made via PayID — a direct bank-to-bank transfer that you initiate from your own banking app. FAT BUSTERS never sees or stores your banking credentials.',
        'Each order is assigned a unique reference code (FB-XXXXXX). You must include this reference in your PayID transfer so we can match your payment to your order.',
        'Orders remain "Pending" until we manually confirm the PayID transfer has landed in our account. We aim to confirm within 24 business hours.',
        'Pricing is in Australian Dollars (AUD) and includes any applicable GST.',
      ]],
      ['Shipping', [
        'See our Shipping & Returns policy for delivery times, regions, and lost-in-transit handling.',
      ]],
      ['Refunds', [
        'See our Refund Policy for the limited circumstances under which we issue refunds.',
      ]],
      ['Account security', [
        'You are responsible for keeping your account credentials secure. Notify us immediately if you suspect unauthorised access.',
      ]],
      ['Acceptable use', [
        'You agree not to resell, repackage, or misrepresent our products.',
        'You agree not to misuse the website (scraping, automation, attacking infrastructure).',
        'We may suspend or terminate accounts that breach these terms.',
      ]],
      ['Liability', [
        'To the maximum extent permitted by Australian law, FAT BUSTERS\' liability for any claim is limited to a refund of amounts you have paid us in the previous 12 months. We are not liable for indirect, consequential, or special damages.',
        'Nothing in these terms limits any rights you have under the Australian Consumer Law that cannot be excluded.',
      ]],
      ['Governing law', [
        'These terms are governed by the laws of the State of New South Wales, Australia. Disputes will be heard in the courts of New South Wales.',
      ]],
      ['Changes', [
        'We may update these terms from time to time. The current version always lives at this URL with a last-updated date.',
      ]],
    ],
  },

  shipping: {
    title: 'Shipping & Returns',
    intro: 'How and when your order ships, and what happens if something goes wrong in transit.',
    updated: '13 February 2026',
    sections: [
      ['When we ship', [
        'Orders are dispatched within 1–2 business days of payment being confirmed (i.e. once your PayID transfer has landed and we have marked your order as Paid).',
        'You will receive an SMS and email when your order moves to Shipped.',
      ]],
      ['Delivery times (estimates)', [
        'Metropolitan (Sydney, Melbourne, Brisbane, Adelaide, Perth, Canberra): 2–4 business days.',
        'Regional Australia: 3–7 business days.',
        'Remote areas (NT, far-north QLD, regional WA): 5–10 business days.',
        'These are courier estimates, not guarantees.',
      ]],
      ['Packaging', [
        'All orders are shipped in plain, unbranded packaging.',
        'Discreet sender details are used on the shipping label.',
        'Vials are packed with insulating material; however we do not ship with ice packs and we do not guarantee in-transit temperature. Lyophilised (powdered) peptides are stable at ambient temperature for short transit periods.',
      ]],
      ['If your order is lost or delayed', [
        'Get in touch via the Contact Us form with your reference number. Provide at least 5 business days from the dispatch date before reporting a metro delay; 10 business days for regional/remote.',
        'If the courier confirms the parcel is lost, we will re-ship at no additional cost.',
      ]],
      ['If your order arrives damaged', [
        'Contact us within 48 hours of delivery with photos of the damaged packaging and product.',
        'We will replace damaged items at no cost.',
      ]],
      ['Returns', [
        'For hygiene and product-integrity reasons, we do not accept returns of opened or unsealed products.',
        'If we have shipped you the wrong item, contact us within 7 days of delivery and we will arrange a replacement.',
      ]],
    ],
  },

  refunds: {
    title: 'Refund Policy',
    intro: 'When refunds are issued, and how they work given our PayID-only payment model.',
    updated: '13 February 2026',
    sections: [
      ['When we issue refunds', [
        'Your order cannot be fulfilled (e.g. unexpected stock-out, regulatory issue).',
        'We shipped you the wrong item and you have returned it (unopened).',
        'Your order was lost in transit and the courier has formally confirmed the loss.',
      ]],
      ['When we do NOT issue refunds', [
        'Change of mind once your order has been shipped.',
        'Buyer\'s remorse on opened or partially-used products.',
        'Failure to follow the dosing & storage guidance in our Dosing & Safety Guide.',
        'Delays caused by the courier that fall within their published delivery windows.',
      ]],
      ['How refunds are processed', [
        'Because all payments are made by PayID directly from your bank account, refunds are issued by manual PayID transfer back to the same account or PayID you paid from. We will email you to confirm the destination account before issuing the refund.',
        'No card processing means there is nothing to "chargeback"; all refunds are bank-to-bank.',
        'Refunds are typically processed within 3 business days of approval.',
      ]],
      ['Disputes', [
        'If you believe a refund decision was unfair, contact us in writing via the Contact Us form. We will respond within 30 days as required by the Australian Consumer Law.',
        'Nothing in this policy limits your statutory rights under the Australian Consumer Law.',
      ]],
    ],
  },
};

const POLICY_SLUGS = Object.keys(POLICIES);

const LegalPage = ({ slug }) => {
  const policy = POLICIES[slug];
  if (!policy) return <Navigate to="/privacy" replace />;

  return (
    <main className="max-w-3xl mx-auto px-5 sm:px-6 py-8 sm:py-12" data-testid={`legal-page-${slug}`}>
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Link to="/" className="hover:text-red-700">Home</Link>
        <ChevronRight size={13} />
        <span>{policy.title}</span>
      </div>

      <header className="mt-5 flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
          <FileText size={22} className="text-red-600" />
        </div>
        <div>
          <h1 className="text-[#0a0a0a] font-bold text-3xl sm:text-4xl tracking-tight">{policy.title}</h1>
          <p className="text-slate-500 mt-2">{policy.intro}</p>
          <p className="text-xs text-slate-400 mt-2">Last updated: {policy.updated}</p>
        </div>
      </header>

      <article className="mt-8 space-y-7 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm">
        {policy.sections.map(([heading, items]) => (
          <section key={heading}>
            <h2 className="text-base font-bold text-[#0a0a0a]">{heading}</h2>
            <ul className="mt-2 space-y-1.5 text-sm text-slate-700 list-disc pl-5 marker:text-red-400">
              {items.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </section>
        ))}
      </article>

      <nav className="mt-6 flex flex-wrap gap-2 text-xs">
        {POLICY_SLUGS.filter((s) => s !== slug).map((s) => (
          <Link key={s} to={`/${s}`} className="px-3 py-1.5 bg-white border border-slate-200 hover:border-red-300 hover:text-red-700 rounded-full text-slate-600">
            {POLICIES[s].title}
          </Link>
        ))}
      </nav>

      <div className="mt-6 text-center">
        <Link to="/" className="text-sm text-slate-500 hover:text-red-600">&larr; Back to shop</Link>
      </div>
    </main>
  );
};

export default LegalPage;
